import { getEnvConfig } from '@hbs-us/contracts';

import { getAppEnv } from '../../lib/env.js';

/**
 * Build the credentialed/public URLs for the invoice download + pay/sign paths
 * (D-06). NONE of these hosts are hardcoded (threat T-21-07): the api host comes
 * from `@hbs-us/contracts getEnvConfig().domains.api`, the public SSR host from
 * `getEnvConfig().domains.app` (the legacy tenant SSR host).
 *
 * - **PDF**: `invoicePdfUrl(id)` → `${apiBase}/api/v1/invoices/:id/pdf` — the
 *   existing api-host GET (engine reused from 21-02, not rebuilt client-side).
 *   Opened via `<a>` / new tab with the user's cookies (safe GET read, no CSRF).
 * - **Pay / sign links**: point at the LEGACY SSR public host token routes — the
 *   unauthenticated Stripe pay page + signature page STAY SSR (Pitfall 2 / A2 —
 *   the SPA never re-mounts those flows; it only links out). These take the
 *   server-minted `public_token`. The public token pages are served from the
 *   tenant's own host (`{tenant}.<env>.qelm.app`), which is the SAME origin the
 *   SPA shell is served from — so they are derived from the current window
 *   origin, NOT a hardcoded host (`getEnvConfig` exposes only the api host, not
 *   a per-tenant SSR host).
 */

function apiBaseUrl(): string {
  return `https://${getEnvConfig(getAppEnv()).domains.api}`;
}

/** The legacy SSR public host = the current tenant origin (same host as the SPA shell). */
function publicBaseUrl(): string {
  if (typeof window !== 'undefined') return window.location.origin;
  return '';
}

/** Credentialed PDF download for an invoice (api-host GET). */
export function invoicePdfUrl(id: number): string {
  return `${apiBaseUrl()}/api/v1/invoices/${id}/pdf`;
}

/** Public Stripe pay page for an invoice (legacy SSR host, by opaque token). */
export function invoicePayUrl(publicToken: string): string {
  return `${publicBaseUrl()}/invoice/pay/${publicToken}`;
}

/** Public signed/sign view for an invoice (legacy SSR host, by opaque token). */
export function invoicePublicViewUrl(publicToken: string): string {
  return `${publicBaseUrl()}/invoice/view/${publicToken}`;
}

/** Credentialed invoice-attachment GET/POST/DELETE base (api-host, 22-21). */
export function invoiceAttachmentUrl(id: number): string {
  return `${apiBaseUrl()}/api/v1/invoices/${id}/attachment`;
}

/** Credentialed payment-attachment GET (api-host, 22-21). */
export function paymentAttachmentUrl(paymentId: number): string {
  return `${apiBaseUrl()}/api/v1/payments/${paymentId}/attachment`;
}
